import React, { useState } from 'react';
import { Box, Checkbox, Dialog, Divider, FormControlLabel, FormGroup, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Text from '../../../components/UI/atoms/Text';
import Button from '../../../components/UI/atoms/Button';
import Card from '../../../components/UI/molecules/Card';
import Input from '../../../components/UI/atoms/Input';
import { authPost } from '../../../services/authCalls';
import { useApp } from '../../../AppContext';

const PLAN_TAGS = [
  { label: 'News & Market',  value: 'TNM_NEWS_MARKET' },
  { label: 'News & Data',    value: 'TNM_NEWS_DATA' },
  { label: 'Drill Hole',     value: 'TNM_DRILLHOLE' },
  { label: 'Basic',          value: 'TNM_BASIC' },
];

const RENEWAL_TYPES = ['Monthly', 'Annual'];

const empty = { firmName: '', email: '', firstName: '', lastName: '', subscriptionTags: [], seatCount: 1, renewalType: 'Monthly' };

export default function CreateAccountModal({ open, onClose }) {
  const { handleMessage } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const toggleTag = (tag) => {
    setForm(p => ({
      ...p,
      subscriptionTags: p.subscriptionTags.includes(tag)
        ? p.subscriptionTags.filter(t => t !== tag)
        : [...p.subscriptionTags, tag],
    }));
  };

  const handleClose = () => {
    setForm(empty);
    onClose();
  };

  const canSave = form.firmName.trim() && form.email.trim() && form.firstName.trim() && form.lastName.trim();

  const save = () => {
    setSaving(true);
    authPost('/api/admin/account/create', {
      firmName: form.firmName.trim(),
      email: form.email.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      subscriptionTags: form.subscriptionTags,
      seatAlloted: Number(form.seatCount) || 1,
      renewalType: form.renewalType,
    })
      .then(res => {
        handleMessage('success', 'Account created');
        handleClose();
        const firmId = res?.data?.firmId;
        if (firmId) navigate(`/admin/firms/${firmId}`);
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Failed to create account';
        handleMessage('error', msg);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <Card sx={{ margin: 0, width: { xs: '340px', sm: '520px' } }}>
        <Text type='h1' sx={{ mb: '12px' }}>Add New Account</Text>
        <Divider sx={{ mb: '20px' }} />

        <Input
          label='Firm Name *'
          id='ca-firmName'
          value={form.firmName}
          onChange={e => set('firmName', e.target.value)}
        />

        <Input
          label='Email *'
          id='ca-email'
          type='email'
          value={form.email}
          onChange={e => set('email', e.target.value)}
        />

        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Box sx={{ flex: 1 }}>
            <Input
              label='First Name *'
              id='ca-firstName'
              value={form.firstName}
              onChange={e => set('firstName', e.target.value)}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Input
              label='Last Name *'
              id='ca-lastName'
              value={form.lastName}
              onChange={e => set('lastName', e.target.value)}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '12px', mt: '8px' }}>
          <Box sx={{ flex: 1 }}>
            <Input
              label='Seat Count'
              id='ca-seatCount'
              type='number'
              value={form.seatCount}
              onChange={e => set('seatCount', e.target.value)}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Input
              label='Renewal Type'
              id='ca-renewalType'
              select
              value={form.renewalType}
              onChange={e => set('renewalType', e.target.value)}
            >
              {RENEWAL_TYPES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Input>
          </Box>
        </Box>

        <Text sx={{ fontSize: '12px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', mt: '8px', mb: '4px' }}>
          Subscription Plans
        </Text>
        <FormGroup row>
          {PLAN_TAGS.map(p => (
            <FormControlLabel
              key={p.value}
              control={
                <Checkbox
                  size='small'
                  checked={form.subscriptionTags.includes(p.value)}
                  onChange={() => toggleTag(p.value)}
                />
              }
              label={<Text sx={{ fontSize: '13px' }}>{p.label}</Text>}
            />
          ))}
        </FormGroup>

        <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', mt: '20px' }}>
          <Button type='outlined' onClick={handleClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving}>
            {saving ? 'Creating…' : 'Create Account'}
          </Button>
        </Box>
      </Card>
    </Dialog>
  );
}
